import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { StepFlow } from "@/components/StepFlow";
import { CountryPicker } from "@/components/CountryPicker";
import { DeviceMobile, Lightning, Gift, Bus, SimCard, PaperPlaneRight } from "@phosphor-icons/react";

const CATS = [
  { id: 1, name: "Mobile", icon: DeviceMobile, route: "/app/topup", sub: "Airtime, data & bundles" },
  { id: 3, name: "Utilities", icon: Lightning, sub: "Electricity, water, gas, internet & more" },
  { id: 4, name: "Gift Cards", icon: Gift, route: "/app/giftcards", sub: "Retail gift cards — delivered by email" },
  { id: 8, name: "Transportation", icon: Bus, sub: "Transit & travel top-ups" },
  { id: 13, name: "eSIM", icon: SimCard, route: "/app/esim", sub: "Travel data plans" },
];

const prettyField = (f) => f.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function DigitalServices() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [cat, setCat] = useState(null);
  const [country, setCountry] = useState("CM");
  const [operator, setOperator] = useState(null);
  const [product, setProduct] = useState(null);
  const [identifiers, setIdentifiers] = useState({});
  const [amount, setAmount] = useState("");
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const { data: ops, isFetching: opsLoading, isError: opsError } = useQuery({
    queryKey: ["svc-ops", cat?.id, country],
    queryFn: async () => (await api.get(`/services/operators?service_id=${cat.id}&country=${country}`)).data,
    enabled: !!cat && step === 1,
  });
  const { data: prods, isFetching: prodLoading } = useQuery({
    queryKey: ["svc-prods", cat?.id, country, operator?.id],
    queryFn: async () => (await api.get(`/services/products?service_id=${cat.id}&country=${country}&operator_id=${operator.id}`)).data,
    enabled: !!operator && step === 1,
  });
  const { data: orders } = useQuery({ queryKey: ["svc-orders"], queryFn: async () => (await api.get("/services/orders")).data });

  const pickCategory = (c) => {
    if (c.route) { navigate(c.route); return; }
    setCat(c); setStep(1); setOperator(null); setProduct(null); setIdentifiers({});
  };

  const pickProduct = (p) => {
    setProduct(p);
    const init = {}; (p.identifier_fields || []).forEach((f) => (init[f] = ""));
    setIdentifiers(init);
    setStep(2);
  };

  const purchase = async () => {
    for (const f of (product.identifier_fields || [])) {
      if (!identifiers[f]?.trim()) { toast.error(`Enter ${prettyField(f)}`); return; }
    }
    if (product.ranged && (!amount || Number(amount) < 1)) { toast.error("Enter an amount (min $1.00)"); return; }
    setBusy(true);
    try {
      const res = await api.post("/services/purchase", {
        product_id: product.id, service_id: cat.id, country, operator_id: operator.id,
        category: cat.name, label: product.label, retail_price_cents: product.ranged ? Math.round(Number(amount) * 100) : product.retail_price_cents,
        amount_cents: product.ranged ? Math.round(Number(amount) * 100) : null,
        identifiers,
      });
      toast.success(res.data?.note || "Order submitted");
      qc.invalidateQueries({ queryKey: ["wallet"] });
      qc.invalidateQueries({ queryKey: ["svc-orders"] });
      setCat(null); setOperator(null); setProduct(null); setIdentifiers({}); setAmount(""); setStep(0);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Send Top-up</h1>
        <p className="mt-2 text-muted-foreground">Mobile, Utilities, Gift Cards, Transportation &amp; eSIM — instant delivery.</p>
      </div>

      {step === 0 && (
        <div className="animate-fade-up grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {CATS.map((c) => (
            <button key={c.id} data-testid={`svc-cat-${c.id}`} onClick={() => pickCategory(c)}
              className="flex items-center gap-4 rounded-2xl border border-border bg-card p-5 text-left transition-all hover:-translate-y-0.5 hover:border-primary">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-primary"><c.icon size={24} weight="duotone" /></div>
              <div><p className="font-semibold">{c.name}</p><p className="text-xs text-muted-foreground">{c.sub}</p></div>
            </button>
          ))}
        </div>
      )}

      {step === 0 && orders?.orders?.length > 0 && (
        <div>
          <h2 className="mb-3 mt-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Recent orders</h2>
          <div className="space-y-2">
            {orders.orders.slice(0, 6).map((o) => {
              const st = (o.status || "").toLowerCase();
              const color = st === "delivered" ? "text-emerald-600" : st === "failed" ? "text-red-500" : st === "reversed" ? "text-amber-600" : "text-muted-foreground";
              return (
                <div key={o.id} data-testid={`order-${o.id}`} className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
                  <div className="min-w-0"><p className="truncate text-sm font-medium">{o.label}</p><p className="text-xs text-muted-foreground">{o.category} · {o.country}</p></div>
                  <div className="text-right"><p className="font-mono-metric text-sm font-semibold">{money(o.amount_cents)}</p><p className={`text-xs font-medium capitalize ${color}`}>{st}</p></div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {step === 1 && cat && (
        <div className="animate-fade-up space-y-4">
          <StepFlow steps={["Category", "Product", "Send"]} current={1} />
          <button onClick={() => { setStep(0); setCat(null); }} className="rounded-full border border-border px-3 py-1.5 text-sm">← {cat.name}</button>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Country</p>
            <CountryPicker value={country} onChange={(c) => { setCountry(c.code); setOperator(null); setProduct(null); }} />
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Provider</p>
            {opsLoading ? <p className="text-sm text-muted-foreground">Loading providers…</p>
              : opsError ? <p data-testid="svc-ops-error" className="text-sm text-red-500">Couldn't load providers.</p>
              : (ops?.operators?.length ? (
                <div className="flex flex-wrap gap-2">
                  {ops.operators.map((o) => (
                    <button key={o.id} data-testid={`svc-op-${o.id}`} onClick={() => { setOperator(o); setProduct(null); }}
                      className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${operator?.id === o.id ? "bg-primary text-primary-foreground" : "border border-border"}`}>{o.name}</button>
                  ))}
                </div>
              ) : <p data-testid="svc-ops-empty" className="text-sm text-muted-foreground">No providers for {cat.name} in this country. Try another country.</p>)}
          </div>
          {operator && (
            prodLoading ? <div className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">Loading products…</div>
            : (prods?.products?.length ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {prods.products.map((p) => (
                  <button key={p.id} data-testid={`svc-product-${p.id}`} onClick={() => pickProduct(p)}
                    className="flex items-center justify-between rounded-2xl border border-border bg-card p-4 text-left transition-colors hover:border-primary">
                    <div className="min-w-0"><p className="text-xs uppercase tracking-wide text-muted-foreground">{p.subservice || p.type}</p><p className="truncate font-medium">{p.label}</p></div>
                    <span className="ml-2 shrink-0 font-mono-metric font-semibold">{money(p.retail_price_cents)}</span>
                  </button>
                ))}
              </div>
            ) : <div data-testid="svc-products-empty" className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">No products for this provider.</div>)
          )}
        </div>
      )}

      {step === 2 && product && (
        <div className="animate-fade-up mx-auto max-w-md space-y-4">
          <StepFlow steps={["Category", "Product", "Send"]} current={2} />
          <div className="rounded-3xl border border-border bg-card p-6">
            <h2 className="font-display text-xl font-semibold">{product.label}</h2>
            <p className="text-sm text-muted-foreground">{operator?.name} · {cat.name}</p>
            {(product.identifier_fields || []).map((f) => (
              <div key={f} className="mt-4">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{prettyField(f)}</label>
                <input data-testid={`svc-field-${f}`} value={identifiers[f] || ""} onChange={(e) => setIdentifiers((s) => ({ ...s, [f]: e.target.value }))}
                  placeholder={f === "mobile_number" ? "+1 555 010 2020" : prettyField(f)}
                  className="mt-1.5 w-full rounded-xl border border-input bg-card px-3 py-2.5 font-mono-metric text-sm outline-none focus:border-primary" />
              </div>
            ))}
            {(product.identifier_fields || []).length === 0 && !product.ranged && (
              <p className="mt-3 rounded-xl bg-secondary p-3 text-xs text-muted-foreground">No recipient details needed — a code/PIN is issued after purchase.</p>
            )}
            {product.ranged && (
              <div className="mt-4">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Amount (USD)</label>
                <input data-testid="svc-amount" type="number" min="1" step="0.5" value={amount} onChange={(e) => setAmount(e.target.value)}
                  placeholder="10.00" className="mt-1.5 w-full rounded-xl border border-input bg-card px-3 py-2.5 font-mono-metric text-sm outline-none focus:border-primary" />
                <p className="mt-1 text-xs text-muted-foreground">This is a variable-amount product — choose how much to send.</p>
              </div>
            )}
            <div className="mt-5 flex justify-between border-t border-border pt-4">
              <span className="font-semibold">Total</span>
              <span className="font-mono-metric font-semibold">{product.ranged ? (amount ? money(Math.round(Number(amount) * 100)) : "—") : money(product.retail_price_cents)}</span>
            </div>
            <div className="mt-5 flex gap-3">
              <button onClick={() => setStep(1)} className="rounded-full border border-border px-5 py-3 text-sm font-medium">Back</button>
              <button data-testid="svc-purchase" onClick={purchase} disabled={busy}
                className="flex flex-1 items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-primary-foreground disabled:opacity-60">
                <PaperPlaneRight size={18} weight="bold" /> {busy ? "Sending…" : "Send now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
